import dayjs from "dayjs";
import React, { useContext, useState, useEffect } from "react";
import ErrorPopup from "../../../../components/element/ErrorPopup";
import Loader from "../../../../components/element/Loader";
import NoDataPlaceholder from "../../../../components/element/NoDataPlaceholder";
import { DATE_FORMAT, LAND_LEASE_TYPE } from "../../../../utils/constants";
import { formatToTwoDecimal, handleEmptyValue } from "../../../../utils/utils";
import "../css/ConfessionFee.css";
import { StorageContext } from "../../../../services/StorageProvider";
import useAirlineAdminApi from "../../../../services/api/useAirlineAdminApi";
import { useMutation } from "@tanstack/react-query";

const ConcessionFeeTable = ({ concessionFeeData, isLoading, error }) => {
  const { airportData } = useContext(StorageContext);
  const { getAirlineById } = useAirlineAdminApi();
  const [airlineNames, setAirlineNames] = useState({});

  // Load cached airline names from localStorage on component mount
  useEffect(() => {
    const cachedNames = localStorage.getItem("airlineNames");
    if (cachedNames) {
      try {
        setAirlineNames(JSON.parse(cachedNames));
      } catch (error) {
        console.error("Error parsing cached airline names:", error);
      }
    }
  }, []);

  // Fetch airline names for items that don't have cached names
  useEffect(() => {
    if (concessionFeeData && concessionFeeData.length > 0) {
      concessionFeeData.forEach((item) => {
        if (item?.airlineId && !airlineNames[item.airlineId]) {
          fetchAirlineNameByIDMutation.mutate(item.airlineId);
        }
      });
    }
  }, [concessionFeeData, airlineNames]);

  const fetchAirlineNameByIDMutation = useMutation({
    mutationFn: async (airlineId) => {
      const response = await getAirlineById(airlineId);
      return { id: airlineId, name: response?.data?.airlineName || "" };
    },
    onSuccess: (data) => {
      setAirlineNames((prev) => {
        const newNames = { ...prev, [data.id]: data.name };
        localStorage.setItem("airlineNames", JSON.stringify(newNames));
        return newNames;
      });
    },
    onError: (error, airlineId) => {
      console.error(`Error fetching airline name for ID ${airlineId}:`, error);
      setAirlineNames((prev) => {
        const newNames = { ...prev, [airlineId]: "" };
        localStorage.setItem("airlineNames", JSON.stringify(newNames));
        return newNames;
      });
    },
    retry: false,
  });

  if (error) {
    if (error.status === 404) {
      return <NoDataPlaceholder />;
    } else {
      return <ErrorPopup children={error.message}></ErrorPopup>;
    }
  }

  if (isLoading) {
    return <Loader />;
  }

  if (concessionFeeData && concessionFeeData.length === 0) {
    return <NoDataPlaceholder />;
  }

  const getAirportName = (airportId) => {
    const airport = airportData?.find((airport) => airport?.id === airportId);
    return airport ? airport.name : "--";
  };

  return (
    <div className="concession-fee-table-parent-container">
      <div className="concession-fee-table-container">
        <table className="concession-fee-table">
          <thead>
            <tr>
              <th>No.</th>
              <th>Airline Name</th>
              <th>Customer ID</th>
              <th>Airport Name</th>
              <th>Submission Date</th>
              <th>Land Use Category</th>
              <th>Lease Start Date</th>
              <th>Lease End Date</th>
              <th>Lease Area(Square ft)</th>
              <th>Total Fees(BSD)</th>
              <th>VAT (10%)</th>
              <th>Grand Total</th>
            </tr>
          </thead>
          <tbody>
            {concessionFeeData?.map((item, index) => (
              <tr key={index}>
                <td>{index + 1}</td>
                <td>
                  {handleEmptyValue(
                    airlineNames[item?.airlineId] || item?.airlineName
                  )}
                </td>
                <td>{handleEmptyValue(item?.dynamicsCustomerNumber)}</td>
                <td>{handleEmptyValue(getAirportName(item?.airportId))}</td>
                <td>
                  {handleEmptyValue(
                    dayjs(item?.submittedAt).format(DATE_FORMAT)
                  )}
                </td>
                <td>
                  {
                    LAND_LEASE_TYPE.find(
                      (status) => status.value === item?.vendorLeaseType
                    )?.name
                  }
                </td>
                <td>
                  {handleEmptyValue(
                    dayjs(item?.leaseStartDate).format(DATE_FORMAT)
                  )}
                </td>
                <td>
                  {handleEmptyValue(
                    dayjs(item?.leaseEndDate).format(DATE_FORMAT)
                  )}
                </td>
                <td>{handleEmptyValue(item?.leasedArea)}</td>
                <td>{formatToTwoDecimal(item?.totalFee)}</td>
                <td>{formatToTwoDecimal(item?.tax)}</td>
                <td>{formatToTwoDecimal(item?.totalFee + item?.tax)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default ConcessionFeeTable;
